import { ListItem, Text, UnorderedList, useDisclosure } from '@chakra-ui/react';
import { ExclamationIcon, IdentificationIcon } from '@heroicons/react/outline';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  IdentityType,
  IdentityVerificationOrigin,
  SubscriptionStepStatus,
} from '@blockpulse3/data/shared';
import {
  MergedIdentityVerificationStep,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { IdentityVerificationModal } from '@blockpulse3/web-client/auth';

import { SubscriptionStep } from '../../SubscriptionStep';

type Props = {
  isDisabled: boolean;
  subscriptionStep: MergedIdentityVerificationStep;
};

let timeoutId: NodeJS.Timeout | null = null;

export function IdentityVerificationStep({ subscriptionStep, isDisabled }: Props): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');
  const i18nUbbleResponseCodes = useTranslations('UbbleResponseCodes');

  const [isPollingStarted, setIsPollingStarted] = useState<boolean>(false);
  const [isPollingLoading, setIsPollingLoading] = useState<boolean>(false);
  const [isPollingDone, setIsPollingDone] = useState(false);

  const [verificationStep, setVerificationStep] = useState<number>(0);

  const { subscriptionId = '', companyId = '' } = useParams();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, refetch, startPolling, stopPolling } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const identity = data?.subscription?.identity;

  const handleVerificationModalOpen = (status: SubscriptionStepStatus): void => {
    if ([SubscriptionStepStatus.ERROR, SubscriptionStepStatus.NONE].includes(status)) {
      if (!companyId) setVerificationStep(1);
      onOpen();
    }
  };

  useEffect(() => {
    if (!isPollingStarted && subscriptionStep.status === SubscriptionStepStatus.IN_PROGRESS) {
      setIsPollingStarted(true);
      setIsPollingLoading(true);
      setIsPollingDone(false);
      startPolling(2000);

      if (timeoutId) clearTimeout(timeoutId);

      /* ** Stop polling after 2min ** */
      timeoutId = setTimeout(() => {
        stopPolling();
        setIsPollingLoading(false);
        setIsPollingDone(true);
      }, 120_000);
    }

    /* ** Stop polling when step status is VALID ** */
    if (isPollingStarted && subscriptionStep.status !== SubscriptionStepStatus.IN_PROGRESS) {
      stopPolling();
      setIsPollingStarted(false);
      setIsPollingLoading(false);
      setIsPollingDone(true);
      refetch();
    }
  }, [isPollingStarted, startPolling, stopPolling, refetch, subscriptionStep.status]);

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const individualIdentity =
    identity?.type === IdentityType.INDIVIDUAL
      ? identity?.individualIdentity
      : identity?.companyIdentity?.kybIndividualIdentity;

  const ubbleResponseCodes = individualIdentity?.ubbleResponseCodes || [];
  const hasWarningMessage =
    !!ubbleResponseCodes.length &&
    individualIdentity?.kycOrigin === IdentityVerificationOrigin.UBBLE &&
    [SubscriptionStepStatus.NONE, SubscriptionStepStatus.ERROR].includes(subscriptionStep.status);

  const RenderChildComponent = (): JSX.Element | null => {
    const isVerificationStalled =
      subscriptionStep.status === SubscriptionStepStatus.IN_PROGRESS && isPollingDone;

    if (isVerificationStalled) {
      return <Text fontSize="sm">{i18nSubscription('PendingVerificationTitle')}</Text>;
    }

    if (hasWarningMessage) {
      return (
        <UnorderedList>
          {ubbleResponseCodes.map((responseCode) => {
            return (
              <ListItem key={responseCode.code} fontSize="sm">
                {i18nUbbleResponseCodes(
                  responseCode.summary.toUpperCase() as keyof IntlMessages['UbbleResponseCodes'],
                )}
              </ListItem>
            );
          })}
        </UnorderedList>
      );
    }

    return null;
  };

  return (
    <>
      <SubscriptionStep
        icon={hasWarningMessage ? ExclamationIcon : IdentificationIcon}
        iconColor={hasWarningMessage ? 'yellow.400' : undefined}
        isBadgeLoading={isPollingLoading}
        isDisabled={isDisabled}
        status={subscriptionStep.status}
        title={i18nSubscription('IdentityVerification')}
        type={subscriptionStep.type}
        onClick={handleVerificationModalOpen}
      >
        <RenderChildComponent />
      </SubscriptionStep>
      <IdentityVerificationModal
        isOpen={isOpen}
        isUserWorkspace={!companyId}
        refetchData={refetch}
        setVerificationStep={setVerificationStep}
        verificationStep={verificationStep}
        onClose={onClose}
      />
    </>
  );
}
